import { useParams, useSearchParams, useNavigate } from 'react-router-dom'
import { useEffect, useState } from 'react'
import * as Sentry from '@sentry/react'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'

import { exchangeLoginToken } from '@/services/contribution/api.tsx'
import useSession from '@/hooks/useSession'
import { getSignInURL } from '@/util/helpers'
import { sendAuthErrorEvent, sendSigninMagicLinkEvent } from '@/services/analytics/gtm'

import LoadingScreen from '@components/LoadingScreen'
import WelcomePage from './WelcomePage'

export default function RootPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { session, startSession, endSession } = useSession()
  const [signingInAs, setSigningInAs] = useState<string | null>(null)
  const { schoolSlug } = useParams()
  const [searchParams] = useSearchParams()
  const loginToken = searchParams.get('login_token')

  useEffect(() => {
    async function login() {
      if (schoolSlug && loginToken) {
        try {
          const { accessToken, eventId, participationId, parentName, parentSlug, schoolName, isParticipationMutable } =
            await exchangeLoginToken(schoolSlug, loginToken)

          endSession()

          startSession({
            accessToken,
            eventId: eventId.toString(),
            participationId: participationId.toString(),
            parentName,
            parentSlug,
            schoolName: schoolName ?? '',
            schoolSlug,
            isParticipationMutable,
          })

          Sentry.logger.info(Sentry.logger.fmt`Authenticated via Login Token Exchange: (${participationId})`)

          sendSigninMagicLinkEvent()

          setSigningInAs(parentName)
          setTimeout(() => navigate(`/${schoolSlug}/onboarding`), 1500)
        } catch (error) {
          let toastMessage = t('content.components.signin.magiclink.failure_expired_toast')

          if (error instanceof Error) {
            Sentry.captureException(error, {
              tags: {
                location: 'Login Token Exchange',
              },
              extra: {
                schoolSlug,
              },
            })

            if (error.message === 'Registration Closed.') {
              toastMessage = `We're sorry. Registration has closed for this event.`
            }

            sendAuthErrorEvent({ action: 'signin_magic_link', errorCause: error.message })
          }
          toast.error(toastMessage, {
            autoClose: false,
          })
        }
      }
    }

    if (!schoolSlug) return

    if (loginToken) {
      login()
    } else if (session?.accessToken) {
      Sentry.logger.info(
        Sentry.logger.fmt`Root page has an Access Token- begin Onboarding (${session?.participationId}).`,
      )
      navigate(`/${schoolSlug}/onboarding`)
    }
  }, [])

  if (!schoolSlug) {
    const signinPage = getSignInURL(schoolSlug)
    Sentry.logger.warn(
      Sentry.logger
        .fmt`School slug is undefined in Root page (${session?.participationId}). Redirecting to ${signinPage}`,
    )
    window.location.replace(signinPage)

    return <LoadingScreen />
  }

  if (signingInAs) {
    return <LoadingScreen title={t('content.components.signin.magiclink.signing_in_as', { parent_name: signingInAs })} />
  }

  return <WelcomePage />
}
